/**
 * LookupPage — Email + license key form to look up a license.
 */

import { useState } from 'react';
import { useNavigate, useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { Search, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { usePortal } from '../context/PortalContext';
import { PRODUCT_DISPLAY } from '../config/products';
import { usePortalPageMeta } from '../hooks/usePortalPageMeta';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LookupPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const deviceCode = searchParams.get('device_code');
    const feature = searchParams.get('feature');
    const source = searchParams.get('source');
    const product = searchParams.get('product');
    const intent = searchParams.get('intent');
    const { actions, meta, state } = usePortal();
    const isExistingActivation = intent === 'activate-existing' && !!deviceCode;
    const productDisplay = product ? PRODUCT_DISPLAY[product] : null;
    const activationName = productDisplay?.activationName || 'this extension';
    const pageTitle = isExistingActivation
        ? `Activate ${activationName} — License Portal — HForgeX`
        : 'Look Up Your License — HForgeX';

    usePortalPageMeta({
        title: pageTitle,
        description: isExistingActivation
            ? `Activate ${activationName} on this browser and send the unlock code back to the extension.`
            : 'Look up your HForgeX license with your purchase email and license key.',
    });

    const [email, setEmail] = useState(location.state?.email || '');
    const [licenseKey, setLicenseKey] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [apiError, setApiError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Only redirect if authenticated AND license data is loaded in memory.
    // Without the license check, a page reload with a valid session token
    // bounces the user to /manage where license is null → dead spinner.
    if (meta.isAuthenticated && state.license) {
        return (
            <Navigate
                to={buildManagePath({ deviceCode, feature, source, product, intent })}
                replace
            />
        );
    }

    const sessionExpired = location.state?.expired;

    function validate() {
        const errors = {};
        const trimmedEmail = email.trim();
        const trimmedKey = licenseKey.trim();

        if (!trimmedEmail) {
            errors.email = 'Email is required.';
        } else if (!EMAIL_RE.test(trimmedEmail)) {
            errors.email = 'Please enter a valid email address.';
        }

        if (!trimmedKey) {
            errors.licenseKey = 'License key is required.';
        }

        return errors;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setApiError(null);

        const errors = validate();
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setIsLoading(true);
        try {
            await actions.lookup(email.trim(), licenseKey.trim());
            navigate(buildManagePath({ deviceCode, feature, source, product, intent }), {
                replace: true,
            });
        } catch (err) {
            if (err.status === 404) {
                setApiError(
                    'No license found for this email and license key. Double-check your details and try again.'
                );
            } else {
                setApiError(err.message || 'Something went wrong. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                    {isExistingActivation
                        ? `Activate ${activationName} on this device`
                        : 'Manage Your License'}
                </h2>
                <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                    {isExistingActivation
                        ? `Enter the email and license key from your purchase. We’ll activate ${activationName} on this browser and send the unlock code back to the extension.`
                        : 'Enter your email and the license key from your purchase confirmation to view your license.'}
                </p>
            </div>

            {sessionExpired && (
                <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 rounded-lg mb-6">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Your session has expired. Please sign in again.</span>
                </div>
            )}

            {location.state?.message && (
                <div className="flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-4 py-3 rounded-lg mb-6">
                    <span>{location.state.message}</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Email */}
                <div>
                    <label
                        htmlFor="email"
                        className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                    >
                        Email Address
                    </label>
                    <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                        autoFocus
                        className={`w-full px-3 py-2.5 text-sm rounded-lg border bg-white dark:bg-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 ${
                            fieldErrors.email
                                ? 'border-red-400 dark:border-red-500'
                                : 'border-zinc-300 dark:border-zinc-600'
                        }`}
                    />
                    {fieldErrors.email && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                            {fieldErrors.email}
                        </p>
                    )}
                </div>

                {/* License Key */}
                <div>
                    <label
                        htmlFor="licenseKey"
                        className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5"
                    >
                        License Key
                    </label>
                    <input
                        id="licenseKey"
                        type="text"
                        value={licenseKey}
                        onChange={(e) => setLicenseKey(e.target.value)}
                        placeholder="From your purchase confirmation email"
                        autoComplete="off"
                        className={`w-full px-3 py-2.5 text-sm rounded-lg border bg-white dark:bg-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 ${
                            fieldErrors.licenseKey
                                ? 'border-red-400 dark:border-red-500'
                                : 'border-zinc-300 dark:border-zinc-600'
                        }`}
                    />
                    {fieldErrors.licenseKey && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                            {fieldErrors.licenseKey}
                        </p>
                    )}
                    <p className="mt-1 text-xs text-zinc-400 dark:text-zinc-500">
                        Find it in your HForgeX or Polar purchase confirmation email. You can paste the full key including dashes.
                    </p>
                </div>

                {/* API error */}
                {apiError && (
                    <div
                        className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg"
                        role="alert"
                    >
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>{apiError}</span>
                    </div>
                )}

                {/* Submit */}
                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Search className="w-4 h-4" />
                    )}
                    {isLoading
                        ? 'Looking up…'
                        : isExistingActivation
                          ? 'Continue to Activation'
                          : 'Look Up License'}
                </button>
            </form>

            {/* Purchase link */}
            <div className="mt-8 pt-6 border-t border-zinc-200 dark:border-zinc-800 text-center">
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    Need a license?{' '}
                    <a
                        href="/purchase"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/purchase');
                        }}
                        className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                    >
                        Get one here
                        <ArrowRight className="w-3 h-3" />
                    </a>
                </p>
            </div>
        </div>
    );
}

function buildManagePath({ deviceCode, feature, source, product, intent }) {
    const params = new URLSearchParams();
    if (deviceCode) params.set('device_code', deviceCode);
    if (product) params.set('product', product);
    if (intent) params.set('intent', intent);
    if (feature) params.set('feature', feature);
    if (source) params.set('source', source);

    const query = params.toString();
    return query ? `/manage?${query}` : '/manage';
}
